import { useState } from "react";
import { Link } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { Bell, Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useGetAdminNotificationsQuery } from "@/redux/slices/notificationApi";

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const { data: response, isLoading } = useGetAdminNotificationsQuery(
    { page: 1, limit: 5 },
    { skip: !open }
  );

  const notifications = response?.data || [];
  const unreadCount = 0; // If you later implement read/unread status, update this

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-9 w-9 rounded-xl hover:bg-primary/5 transition-colors">
          <Bell className="h-4 w-4" />
          {/* Notification badge dot - show if there are unread notifications, currently always showing as an accent */}
          <span className="absolute right-2.5 top-2.5 h-1.5 w-1.5 rounded-full bg-primary ring-2 ring-background" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 rounded-2xl p-0 shadow-2xl border-none ring-1 ring-black/5">
        <DropdownMenuLabel className="flex items-center justify-between p-4 border-b">
          <span className="font-bold text-foreground">Recent Sent Notifications</span>
        </DropdownMenuLabel>
        
        <DropdownMenuGroup className="max-h-[300px] overflow-y-auto">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center p-6 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin mb-2" />
              <span className="text-xs">Loading history...</span>
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-6 text-muted-foreground">
              <Bell className="h-8 w-8 mb-2 opacity-20" />
              <span className="text-sm">No recent notifications</span>
            </div>
          ) : (
            notifications.map((notification) => (
              <DropdownMenuItem key={notification.id} asChild className="cursor-pointer focus:bg-muted/50 rounded-none border-b last:border-0 p-3 items-start flex-col gap-1">
                <Link to={`/notifications/${notification.id}`}>
                  <div className="flex items-start w-full gap-3">
                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Send className="h-4 w-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0 space-y-1">
                      <p className="text-sm font-semibold leading-none truncate text-foreground">
                        {notification.title}
                      </p>
                      <p className="text-xs text-muted-foreground line-clamp-2 leading-snug">
                        {notification.body}
                      </p>
                      <p className="text-[10px] text-muted-foreground/80 font-medium">
                        {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                      </p>
                    </div>
                  </div>
                </Link>
              </DropdownMenuItem>
            ))
          )}
        </DropdownMenuGroup>
        
        <DropdownMenuSeparator className="m-0" />
        <div className="p-2">
          <Button variant="ghost" className="w-full text-xs font-semibold h-8 rounded-xl" asChild>
            <Link to="/notifications">View All Notifications</Link>
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
